import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Building2, ExternalLink, ArrowLeft } from 'lucide-react';
import { useAuth, UserType } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { cn, getSafeRedirect } from '@/lib/utils';
import { toast } from 'sonner';
import BusinessOnboardingModal from '@/components/BusinessOnboardingModal';
import TermsAcceptanceModal from '@/components/TermsAcceptanceModal';
import logo from '@/assets/logo-small.webp';
import i18n from '@/i18n';
import { supabase } from '@/integrations/supabase/client';

// Check if running in iframe
const getIsInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

const AuthPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, updateProfile, isAuthenticated, userType, isLoading, user } = useAuth();
  
  const [isInIframe, setIsInIframe] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loginUserType, setLoginUserType] = useState<UserType | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [selectedUserType, setSelectedUserType] = useState<UserType>('consumer');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [regTermsAccepted, setRegTermsAccepted] = useState(false);
  const [regPrivacyAccepted, setRegPrivacyAccepted] = useState(false);

  // Check if in iframe
  useEffect(() => {
    setIsInIframe(getIsInIframe());
  }, []);

  // Handle redirect parameter from protected routes
  const redirectPath = searchParams.get('redirect');

  // Redirect if already authenticated (wait for profile to load)
  useEffect(() => {
    if (isLoading) return;
    
    if (isAuthenticated && user) {
      // Check terms acceptance first (for both user types)
      if (!user.terms_accepted_at) {
        setShowTerms(true);
        return;
      }
      // Business user who hasn't completed onboarding
      if (userType === 'business' && !user.is_onboarded) {
        setShowOnboarding(true);
      } else if (!showOnboarding) {
        const destination = '/home';
        navigate(destination);
      }
    }
  }, [isAuthenticated, userType, navigate, isLoading, user, showOnboarding, redirectPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (isLogin) {
        const { error } = await login(email, password, loginUserType || 'consumer');
        if (error) {
          toast.error(t('login_error') || 'Kirjautuminen epäonnistui. Tarkista sähköposti ja salasana.');
        }
        // Navigation handled by useEffect
      } else {
        const { error } = await register(email, password, name, 'consumer');
        if (error) {
          if (error.message.includes('already registered')) {
            toast.error(t('email_already_registered') || 'Sähköposti on jo rekisteröity.');
          } else {
            toast.error(t('register_error') || 'Rekisteröinti epäonnistui.');
          }
        } else {
          // Save terms_accepted_at after successful registration
          // Wait a moment for the profile to be created by trigger
          setTimeout(async () => {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              if (session?.user) {
                await supabase
                  .from('profiles')
                  .update({ terms_accepted_at: new Date().toISOString() })
                  .eq('id', session.user.id);
              }
            } catch (err) {
              console.error('Terms update error:', err);
            }
          }, 500);
          
          toast.success(t('registration_success') || 'Rekisteröinti onnistui!');
          // Send welcome email (fire-and-forget)
          try {
            await supabase.functions.invoke('send-welcome-email', {
              body: { email, name, language: i18n.language },
            });
          } catch (welcomeErr) {
            console.error('Welcome email error:', welcomeErr);
          }
          // Onboarding is handled by useEffect when profile loads
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(t('auth_error') || 'Tapahtui virhe. Yritä uudelleen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error(t('enter_email') || 'Syötä sähköpostiosoite');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        toast.error(t('reset_email_error') || 'Virhe salasanan palautuksessa');
      } else {
        toast.success(t('reset_email_sent') || 'Palautuslinkki lähetetty sähköpostiisi!');
        setShowForgotPassword(false);
        setForgotPasswordEmail('');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(t('reset_email_error') || 'Virhe salasanan palautuksessa');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnboardingComplete = async (data: {
    companyName: string;
    operatingArea: string;
    phone: string;
    email: string;
    description: string;
  }) => {
    await updateProfile({
      company_name: data.companyName,
      operating_area: data.operatingArea,
      phone: data.phone,
      contact_email: data.email,
      description: data.description,
      is_onboarded: true,
    });
    setShowOnboarding(false);
    navigate('/home');
  };

  const handleBackToLoginSelection = () => {
    setLoginUserType(null);
    setEmail('');
    setPassword('');
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10 flex gap-2">
        <button
          onClick={() => handleLanguageChange('fi')}
          className={cn(
            "w-10 h-10 rounded-full overflow-hidden border-2 transition-all shadow-md flex items-center justify-center bg-card",
            i18n.language === 'fi' ? "border-gold ring-2 ring-gold/30" : "border-foreground/30 opacity-70 hover:opacity-100"
          )}
          title="Suomi"
        >
          <span className="text-sm font-bold text-foreground">FI</span>
        </button>
        <button
          onClick={() => handleLanguageChange('en')}
          className={cn(
            "w-10 h-10 rounded-full overflow-hidden border-2 transition-all shadow-md flex items-center justify-center bg-card",
            i18n.language === 'en' ? "border-gold ring-2 ring-gold/30" : "border-foreground/30 opacity-70 hover:opacity-100"
          )}
          title="English"
        >
          <span className="text-sm font-bold text-foreground">EN</span>
        </button>
        <button
          onClick={() => handleLanguageChange('sv')}
          className={cn(
            "w-10 h-10 rounded-full overflow-hidden border-2 transition-all shadow-md flex items-center justify-center bg-card",
            i18n.language === 'sv' ? "border-gold ring-2 ring-gold/30" : "border-foreground/30 opacity-70 hover:opacity-100"
          )}
          title="Svenska"
        >
          <span className="text-sm font-bold text-foreground">SV</span>
        </button>
      </div>

      {/* Hero Section with Logo */}
      <div className="gradient-hero text-primary-foreground px-6 pt-14 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col items-center"
        >
          <img 
            src={logo} 
            alt="Reserve" 
            width={64}
            height={64}
            className="h-16 w-auto mb-4"
            fetchPriority="high"
            decoding="sync"
          />
          <p className="text-primary-foreground/80 text-lg text-center font-display">
            {t('welcome_subtitle')}
          </p>
          
          {/* Iframe warning for testing */}
          {isInIframe && (
            <button
              onClick={() => window.open(window.location.origin + '/auth', '_blank')}
              className="mt-4 flex items-center gap-2 text-xs text-primary-foreground/70 hover:text-primary-foreground transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              {t('open_in_new_tab_hint') || 'Avaa erilliseen välilehteen (suositus maksutestaukseen)'}
            </button>
          )}
        </motion.div>
      </div>

      {/* Auth Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="flex-1 px-6 py-8 -mt-6 bg-card rounded-t-3xl"
      >
        {/* Toggle between Login and Register */}
        <div className="flex bg-muted rounded-xl p-1 mb-8">
          <button
            onClick={() => {
              setIsLogin(true);
              setLoginUserType(null);
            }}
            className={cn(
              "flex-1 py-3 rounded-lg font-medium transition-all",
              isLogin ? "bg-card shadow-sm" : "text-muted-foreground"
            )}
          >
            {t('login')}
          </button>
          <button
            onClick={() => setIsLogin(false)}
            className={cn(
              "flex-1 py-3 rounded-lg font-medium transition-all",
              !isLogin ? "bg-card shadow-sm" : "text-muted-foreground"
            )}
          >
            {t('register')}
          </button>
        </div>

        {/* Forgot Password View */}
        {showForgotPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <button
              onClick={() => setShowForgotPassword(false)}
              className="text-sm text-primary font-medium mb-4 flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('back')}
            </button>
            
            <h2 className="text-xl font-display font-semibold mb-2">
              {t('reset_password_title')}
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              {t('reset_password_desc')}
            </p>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('email')}</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="pl-12 h-12 rounded-xl"
                    placeholder="sinun@email.com"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 rounded-xl shadow-button mt-6"
              >
                {isSubmitting ? t('loading') : t('send_reset_link')}
              </Button>
            </form>
          </motion.div>
        )}

        {/* Login View */}
        {isLogin && !showForgotPassword && (
          <>
            {/* Login Type Selection */}
            {loginUserType === null ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-display font-semibold text-center mb-6">
                  {t('select_user_type')}
                </h2>
                <button
                  onClick={() => setLoginUserType('consumer')}
                  className="w-full flex items-center p-5 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all"
                >
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                    <User className="w-7 h-7 text-primary" />
                  </div>
                  <div className="text-left">
                    <span className="font-semibold text-lg block">{t('login_as_consumer')}</span>
                    <span className="text-sm text-muted-foreground">{t('consumer_desc')}</span>
                  </div>
                </button>
                <button
                  onClick={() => setLoginUserType('business')}
                  className="w-full flex items-center p-5 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all"
                >
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                    <Building2 className="w-7 h-7 text-primary" />
                  </div>
                  <div className="text-left">
                    <span className="font-semibold text-lg block">{t('login_as_business')}</span>
                    <span className="text-sm text-muted-foreground">{t('business_desc')}</span>
                  </div>
                </button>

                {/* Business contact info */}
                <div className="mt-2 bg-muted/50 rounded-xl p-4 border border-border">
                  <p className="text-sm text-muted-foreground">
                    {t('business_registration_disabled')}
                  </p>
                  <a
                    href="mailto:reserve@app.fi"
                    className="text-sm text-primary font-medium hover:underline mt-1 inline-block"
                  >
                    {t('contact_for_business_account')}
                  </a>
                </div>

                {/* Browse without login */}
                <div className="mt-6 text-center">
                  <button
                    onClick={() => navigate('/discover')}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
                  >
                    {t('browse_without_login')}
                  </button>
                </div>
              </motion.div>
            ) : (
              /* Login Form */
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <button
                  onClick={handleBackToLoginSelection}
                  className="text-sm text-primary font-medium mb-4 flex items-center"
                >
                  ← {t('back')}
                </button>
                
                <div className="flex items-center mb-6 p-3 bg-muted rounded-xl">
                  {loginUserType === 'consumer' ? (
                    <User className="w-5 h-5 text-primary mr-2" />
                  ) : (
                    <Building2 className="w-5 h-5 text-primary mr-2" />
                  )}
                  <span className="font-medium">
                    {loginUserType === 'consumer' ? t('consumer') : t('business')}
                  </span>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('email')}</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-12 h-12 rounded-xl"
                        placeholder="sinun@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('password')}</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-12 h-12 rounded-xl"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                  </div>

                  <button 
                    type="button" 
                    onClick={() => {
                      setShowForgotPassword(true);
                      setForgotPasswordEmail(email);
                    }}
                    className="text-sm text-primary font-medium"
                  >
                    {t('forgot_password')}
                  </button>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 rounded-xl shadow-button mt-6"
                  >
                    {isSubmitting ? t('loading') : t('login')}
                  </Button>
                </form>
              </motion.div>
            )}
          </>
        )}

        {/* Register View */}
        {!isLogin && !showForgotPassword && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-2"
              >
                <label className="text-sm font-medium">{t('name')}</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-12 h-12 rounded-xl"
                    placeholder="Matti Meikäläinen"
                required
                  />
                </div>
              </motion.div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('email')}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 rounded-xl"
                  placeholder="sinun@email.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('password')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 h-12 rounded-xl"
                  placeholder="••••••••"
                  minLength={6}
                  required
                />
              </div>
            </div>

            {/* Terms checkboxes for consumers */}
            <div className="space-y-3 mt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={regTermsAccepted}
                  onCheckedChange={(checked) => setRegTermsAccepted(checked === true)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <span className="text-sm">{t('accept_terms_register')}</span>
                  <button
                    type="button"
                    onClick={() => window.open('/legal/consumer-terms', '_blank')}
                    className="flex items-center gap-1 text-xs text-primary mt-0.5 hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {t('read_terms')}
                  </button>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={regPrivacyAccepted}
                  onCheckedChange={(checked) => setRegPrivacyAccepted(checked === true)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <span className="text-sm">{t('accept_privacy_register')}</span>
                  <button
                    type="button"
                    onClick={() => window.open('/legal/consumer-privacy', '_blank')}
                    className="flex items-center gap-1 text-xs text-primary mt-0.5 hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {t('read_privacy')}
                  </button>
                </div>
              </label>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting || !regTermsAccepted || !regPrivacyAccepted}
              className="w-full h-12 rounded-xl shadow-button mt-6"
            >
              {isSubmitting ? t('loading') : t('create_account')}
            </Button>
          </form>
        )}

        {/* Toggle Link */}
        <p className="text-center text-muted-foreground mt-6">
          {isLogin ? t('no_account') : t('have_account')}{' '}
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setLoginUserType(null);
            }}
            className="text-primary font-semibold"
          >
            {isLogin ? t('register') : t('login')}
          </button>
        </p>
      </motion.div>

      {/* Terms Acceptance Modal */}
      {showTerms && user && (
        <TermsAcceptanceModal
          userType={user.user_type}
          onAccepted={() => {
            setShowTerms(false);
            // After terms accepted, check if business needs onboarding
            if (userType === 'business' && !user.is_onboarded) {
              setShowOnboarding(true);
            } else {
              const destination = getSafeRedirect(redirectPath);
              navigate(destination);
            }
          }}
        />
      )}

      {/* Business Onboarding Modal */}
      <BusinessOnboardingModal
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
      />
    </div>
  );
};

export default AuthPage;
